<template>
  <div>
    <div class="mb-4">
      <div class="title">{{ offer.offer.default.name }}</div>
      <div class="subtitle">{{ formatDiffKey('offerTypes', offer.type) }} offer</div>
    </div>
    <geo-tabs
      v-model="tab"
      :countries="predefinedCountries"
      :is-disabled="isDisabled"
      @add="onAddCountry"
      @delete="onDeleteCountry"
    />
    <v-card class="mb-8" outlined>
      <v-card-text class="pa-2">
        <div class="subtitle-2">Default URL</div>
        <div class="caption mb-2">
          This URL is used if a matching language doesn't have an affiliate URL
        </div>
        <div class="d-flex align-center">
          <text-input
            :disabled="isDisabled"
            :value="linksUpdatedByGeo.default"
            class="flex-grow-1"
            placeholder="Default affiliate URL"
            outlined
            hide-details
            @input="onInputLink('default', $event)"
          />
          <link-status class="ml-2" :value="linksUpdatedByGeo.default" />
          <uptime-percentage class="ml-2" :stats="statusForLanguage(linksUpdatedByGeo.default)" />
        </div>
      </v-card-text>
    </v-card>
    <div v-for="language in languageOptions" :key="`${offer.slug}-${language.value}`" class="d-flex align-center mt-2">
      <v-tooltip nudge-left="20" right>
        <template v-slot:activator="{ on }">
          <div class="language-key" v-on="on">{{ language.value.toUpperCase() }}</div>
        </template>
        <span>{{ language.key }}</span>
      </v-tooltip>
      <text-input
        :disabled="isDisabled"
        :placeholder="`${language.key} affiliate URL`"
        :rules="[rules.validUrl]"
        :value="linksUpdatedByGeo[language.value]"
        class="flex-grow-1"
        hide-details
        outlined
        @input="onInputLink(language.value, $event)"
      />
      <link-status class="ml-2" :value="linksUpdatedByGeo[language.value]" />
      <uptime-percentage class="ml-2" :stats="statusForLanguage(linksUpdatedByGeo[language.value])" />
    </div>
  </div>
</template>

<script>
// Helpers
import { cloneDeep, merge } from 'lodash-es'
import { mapGetters } from 'vuex'
// Components
import GeoTabs from '@/components/geo-tabs'
import LinkStatus from '@/views/links/components/link-status'
import TextInput from '@/components/forms/text-input'
import UptimePercentage from '@/views/links/components/uptime-percentage'
// Mixins
import DataModification from '@/mixins/data-modification'
// Services
import { languages } from '@/services/offers'

export default {
  name: 'OfferBlock',
  components: {
    GeoTabs,
    LinkStatus,
    TextInput,
    UptimePercentage,
  },
  mixins: [DataModification],
  model: {
    prop: 'group',
  },
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
    offer: {
      type: Object,
      required: true,
    },
    partner: {
      type: Object,
      required: true,
    },
    linkStatusStats: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    countryToAdd: '',
    languageOptions: [],
    tab: 0,
    rules: {
      validUrl: value => {
        if (!value) return true
        try {
          const url = new URL(value)
          if (url.protocol === 'https:') return true
        } catch (err) {}
        return 'Invalid Url'
      },
    },
  }),
  computed: {
    ...mapGetters('options', ['getItemsForOption']),
    currentCountry() {
      return this.predefinedCountries[this.tab]
    },
    currentCountryFormatted() {
      return this.formatDiffKey('countries', this.currentCountry)
    },
    predefinedCountries() {
      return this.group ? Object.keys(this.group.links).sort(country => (country === 'default' ? -1 : 0)) : ['default']
    },
    linksUpdatedByGeo() {
      return this.group ? this.group.links[this.currentCountry] : {}
    },
  },
  watch: {
    offer: {
      handler(currentOffer, oldOffer) {
        if (currentOffer.slug === oldOffer.slug) return
        this.tab = 0
      },
      deep: true,
    },
  },
  async created() {
    await this.fetchLanguageOptions()
  },
  methods: {
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    statusForLanguage(link) {
      if (!link) return { errors: 0, total: 0 }
      const status = this.linkStatusStats.find(({ url }) => url === link)
      if (!status) return { errors: 0, total: 0 }
      return status
    },
    onAddCountry(country) {
      const updatedGroup = merge({}, this.group, { links: { [country]: {} } })
      this.$emit('input', updatedGroup)
    },
    onDeleteCountry(country) {
      const updatedGroup = cloneDeep(this.group)
      delete updatedGroup.links[country]
      this.$emit('input', updatedGroup)
    },
    onInputLink(translationLanguage, value) {
      const updatedGroup = merge({}, this.group, {
        offer: this.offer.slug,
        partner: this.partner.slug,
        links: { [this.currentCountry]: { [translationLanguage]: value || null } },
      })
      this.$emit('input', updatedGroup)
    },
  },
}
</script>

<style lang="scss" scoped>
.language-key {
  min-width: 40px;
}

.tab-styles {
  min-width: 0;
}

.country-select {
  min-width: 200px;
}
</style>
